import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Typography,
  Switch,
  TextField,
  CircularProgress,
  Alert,
  Chip,
  Tooltip,
  IconButton,
  Collapse,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  FormControlLabel,
  Checkbox,
} from "@mui/material";
import SaveIcon from "@mui/icons-material/Save";
import EditIcon from "@mui/icons-material/Edit";
import CheckIcon from "@mui/icons-material/Check";
import api from "../../utils/api";

const SHOW_OPTIONS = [
  { key: "show_in_members", label: "Profile" },
  { key: "show_in_arc", label: "ARC" },
  { key: "show_in_violations", label: "Violations" },
  { key: "show_in_workorders", label: "Work Orders" },
];

const FieldRow = ({ field, onSave }) => {
  const [editing, setEditing] = useState(false);
  const [label, setLabel] = useState(field.display_label || field.field_key);
  const [active, setActive] = useState(!!field.is_active);
  const [showIn, setShowIn] = useState({
    show_in_members: field.show_in_members !== false,
    show_in_arc: field.show_in_arc !== false,
    show_in_violations: field.show_in_violations !== false,
    show_in_workorders: field.show_in_workorders !== false,
  });
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.put(`/custom-field-config/${field.field_key}`, {
        display_label: label,
        is_active: active,
        sort_order: field.sort_order || 0,
        ...showIn,
      });
      onSave();
      setEditing(false);
    } catch {
      // ignore
    } finally {
      setSaving(false);
    }
  };

  const handleToggle = async (newActive) => {
    setActive(newActive);
    setSaving(true);
    try {
      await api.put(`/custom-field-config/${field.field_key}`, {
        display_label: label,
        is_active: newActive,
        sort_order: field.sort_order || 0,
        ...showIn,
      });
      onSave();
    } catch {
      setActive(!newActive);
    } finally {
      setSaving(false);
    }
  };

  return (
    <TableRow hover sx={{ "& td": { py: 1 } }}>
      <TableCell sx={{ width: 48 }}>
        <Switch
          size="small"
          checked={active}
          onChange={(e) => handleToggle(e.target.checked)}
          disabled={saving}
          color="primary"
        />
      </TableCell>
      <TableCell sx={{ minWidth: 130, color: "#6b778c", fontSize: "0.78rem" }}>
        {field.field_key}
        <Chip
          label={`${field.non_empty_count || 0} records`}
          size="small"
          sx={{ ml: 1, fontSize: "0.7rem", height: 18, bgcolor: "#f0f4fa" }}
        />
      </TableCell>
      <TableCell sx={{ minWidth: 200 }}>
        {editing ? (
          <TextField
            size="small"
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            sx={{ width: "100%" }}
            autoFocus
          />
        ) : (
          <Typography variant="body2" sx={{ fontWeight: active ? 600 : 400 }}>
            {label}
          </Typography>
        )}
      </TableCell>
      <TableCell>
        <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
          {SHOW_OPTIONS.map((opt) => (
            <FormControlLabel
              key={opt.key}
              label={<Typography variant="caption">{opt.label}</Typography>}
              control={
                <Checkbox
                  size="small"
                  checked={!!showIn[opt.key]}
                  disabled={!active}
                  onChange={(e) =>
                    setShowIn((p) => ({ ...p, [opt.key]: e.target.checked }))
                  }
                  sx={{ p: 0.5 }}
                />
              }
              sx={{ m: 0 }}
            />
          ))}
        </Box>
      </TableCell>
      <TableCell align="right" sx={{ width: 80 }}>
        {editing ? (
          <>
            <Tooltip title="Save">
              <IconButton size="small" onClick={handleSave} disabled={saving}>
                {saving ? <CircularProgress size={14} /> : <CheckIcon fontSize="small" />}
              </IconButton>
            </Tooltip>
          </>
        ) : (
          <Tooltip title="Edit label & visibility">
            <IconButton size="small" onClick={() => setEditing(true)}>
              <EditIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
      </TableCell>
    </TableRow>
  );
};

const CustomFieldsPanel = () => {
  const [fields, setFields] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showInactive, setShowInactive] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get("/custom-field-config");
      setFields(res.data || []);
    } catch {
      setError("Failed to load custom fields");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const visible = showInactive ? fields : fields.filter((f) => f.is_active || !f.id);
  const active = fields.filter((f) => f.is_active);

  return (
    <Box>
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2 }}>
        <Box>
          <Typography variant="subtitle1" sx={{ fontWeight: 700, color: "#223142" }}>
            DataMart Custom Fields
          </Typography>
          <Typography variant="body2" sx={{ color: "#6b778c" }}>
            Toggle which Jonas/DataMart custom fields appear across member profile, ARC requests, violations, and work orders.
          </Typography>
        </Box>
        {active.length > 0 && (
          <Chip label={`${active.length} active`} color="primary" size="small" />
        )}
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <FormControlLabel
        label="Show all fields (including unused)"
        control={
          <Switch
            size="small"
            checked={showInactive}
            onChange={(e) => setShowInactive(e.target.checked)}
          />
        }
        sx={{ mb: 2 }}
      />

      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: "10px" }}>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: "#f5f7fb" }}>
                <TableCell sx={{ fontWeight: 700, fontSize: "0.78rem" }}>Active</TableCell>
                <TableCell sx={{ fontWeight: 700, fontSize: "0.78rem" }}>Field Key</TableCell>
                <TableCell sx={{ fontWeight: 700, fontSize: "0.78rem" }}>Display Label</TableCell>
                <TableCell sx={{ fontWeight: 700, fontSize: "0.78rem" }}>Show In</TableCell>
                <TableCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {visible.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5}>
                    <Typography variant="body2" sx={{ color: "#6b778c", textAlign: "center", py: 2 }}>
                      No fields found. Toggle "Show all fields" to see available DataMart fields.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                visible.map((f) => (
                  <FieldRow key={f.field_key} field={f} onSave={load} />
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
};

export default CustomFieldsPanel;
